import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { FormPasswordConfirmationModalData } from 'dashboard/forms/FormPasswordConfirmationModal';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { WebhookEvent } from 'dashboard/models/webhook-event';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearWebhookEvent } from 'dashboard/redux/slices/webhook-event';
import { RootState } from 'dashboard/redux/store';
import { loadWebhookEvent, rerunWebhookEvent } from 'dashboard/redux/thunk-actions/webhook-event';
import DashboardWebhookEventsShowView from 'dashboard/views/DashboardWebhookEventsShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = { webhookEventId: string };
const DashboardWebhookEventsShowRoute = (props: PropsFromRedux) => {
  useOnMountAndUnmount(props.clearWebhookEvent);
  useRedirectOnEnvironmentChange('/dashboard/webhooks');

  const { webhookEventId } = useParams<Params>() as Params; // webhookEventId will always be defined

  const { loading: isLoadingWebhookEvent, callback: loadWebhookEvent } = useApiLoader({
    loader: () => props.loadWebhookEvent(webhookEventId),
    deps: [props.loadWebhookEvent, webhookEventId],
    onNetworkError: () => props.showMessage('Failed to load webhook event.', MessageType.Failure),
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load webhook event.', MessageType.Failure);
      }
    },
  });

  // ==============================
  // Rerun Webhook Event
  // ==============================
  const { error: rerunWebhookEventError, callback: handleRerunWebhookEvent } = useApiCallback(
    async (webhookEvent: WebhookEvent, meta?: FormPasswordConfirmationModalData) => {
      await props.rerunWebhookEvent(webhookEvent.id, {
        // Note this will be blank if organization has SAML rerund.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'password-confirmation': meta?.['password-confirmation'],
      });

      loadWebhookEvent();
      props.showMessage('Re-run webhook event', MessageType.Success);
    },
    [props.rerunWebhookEvent, props.showMessage, loadWebhookEvent],
  );

  useApiErrorHandler({
    error: rerunWebhookEventError,
    onError: () => {
      props.showMessage('Error encountered when re-running webhook event.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Render
  // ==============================
  if (props.webhookEvent == null) {
    if (isLoadingWebhookEvent) {
      return <LoadingIndicator fullscreen={true} />;
    } else {
      return (
        <Backdrop>
          <NullState
            message='We couldn’t find that webhook event.'
            backRoute='/dashboard/webhooks'
          />
        </Backdrop>
      );
    }
  }

  return (
    <DashboardWebhookEventsShowView
      organization={props.organization}
      webhookEvent={props.webhookEvent}
      handleRerunWebhookEvent={handleRerunWebhookEvent}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  organization: state.organization.organization,
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  webhookEvent: state.webhookEvent.webhookEvent,
});

const mapDispatchToProps = {
  loadWebhookEvent,
  clearWebhookEvent,
  rerunWebhookEvent,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWebhookEventsShowRoute);
